import React from 'react';
import { Modal, Button, Spinner, Alert } from '@dopomogai/ui';

export interface SecretUsageToolRef {
  tool_id: string;
  tool_name?: string | null;
  env_var_names?: string[] | null;
}

export interface SecretUsageMcpRef {
  server_id: string;
  server_name?: string | null;
  header_keys?: string[] | null;
}

export interface SecretUsageData {
  secret_id: string;
  name: string;
  usage_count?: number | null;
  last_used_at?: string | null;
  tools?: SecretUsageToolRef[];
  mcp_servers?: SecretUsageMcpRef[];
}

export interface SecretUsagePanelProps {
  isOpen: boolean;
  onClose: () => void;
  loading: boolean;
  error?: string | null;
  data?: SecretUsageData | null;
}

export const SecretUsagePanel: React.FC<SecretUsagePanelProps> = ({
  isOpen, onClose, loading, error, data
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Usage: ${data?.name || ''}`}
      size="lg"
      footer={
        <div className="flex justify-end">
          <Button variant="primary" onClick={onClose}>Close</Button>
        </div>
      }
    >
      {loading ? (
        <div className="py-8 flex items-center justify-center">
          <Spinner text="Loading usage…" />
        </div>
      ) : error ? (
        <Alert type="error" title="Failed to load usage" message={error} />
      ) : !data ? (
        <div className="text-sm text-slate-500">No data.</div>
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-slate-700">
            <div>Usage count: {data.usage_count ?? 0}</div>
            <div>Last used: {data.last_used_at ? new Date(data.last_used_at).toLocaleString() : 'Never'}</div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-800 mb-2">Tools</h4>
            {data.tools?.length ? (
              <ul className="space-y-2">
                {data.tools.map((t) => (
                  <li key={t.tool_id} className="p-2 border rounded bg-slate-50">
                    <div className="font-semibold text-sm">{t.tool_name || t.tool_id}</div>
                    <div className="text-xs text-slate-600">Env vars: {t.env_var_names?.join(', ') || '—'}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-slate-500">No tool usages.</div>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-800 mb-2">MCP servers</h4>
            {data.mcp_servers?.length ? (
              <ul className="space-y-2">
                {data.mcp_servers.map((m) => (
                  <li key={m.server_id} className="p-2 border rounded bg-slate-50">
                    <div className="font-semibold text-sm">{m.server_name || m.server_id}</div>
                    <div className="text-xs text-slate-600">Headers: {m.header_keys?.join(', ') || '—'}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-slate-500">No MCP usages.</div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};